import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Coffee, Leaf, Truck, Plus, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import BrandMarquee from "@/components/BrandMarquee";
import Seo from "@/components/Seo";

const HERO_IMG = "https://images.pexels.com/photos/1235706/pexels-photo-1235706.jpeg";
const LIFESTYLE = "https://images.pexels.com/photos/38217301/pexels-photo-38217301.jpeg";
const MARBLE = "https://images.pexels.com/photos/34531681/pexels-photo-34531681.jpeg";

const BENEFITS = [
  { icon: Coffee, n: "01", t: "Calitate premium", d: "Cafea originală, atent selecționată de la branduri recunoscute internațional." },
  { icon: Leaf, n: "02", t: "Prospețime păstrată", d: "Produse ambalate corespunzător pentru a-și păstra aroma și caracterul până la ultima ceașcă." },
  { icon: Truck, n: "03", t: "Livrare rapidă", d: "Expediem rapid și în siguranță, oriunde în țară. Termen estimat de livrare: 1–3 zile lucrătoare." },
];

function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  return (
    <div className="max-w-3xl mx-auto mt-14 divide-y divide-line border-y border-line" data-testid="faq-accordion">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.id || i} className="py-1">
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              data-testid={`faq-item-${i}`}
              className="w-full flex items-start justify-between gap-6 text-left py-5 group"
              aria-expanded={isOpen}
            >
              <span className="font-display text-lg md:text-xl text-ink group-hover:text-roast transition-colors">{f.question}</span>
              <span className="shrink-0 mt-1 text-roast">{isOpen ? <Minus size={20} /> : <Plus size={20} />}</span>
            </button>
            <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
              <div className="overflow-hidden">
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed pb-6 pr-10">{f.answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Home() {
  const [popular, setPopular] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [faq, setFaq] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  useEffect(() => {
    api.get("/products?sort=popular").then(({ data }) => setPopular(data.slice(0, 6)));
    api.get("/settings").then(({ data }) => setRecommended(data.recommended || []));
    api.get("/faq").then(({ data }) => setFaq(data || []));
  }, []);

  const showcase = recommended.length >= 2 ? recommended : popular;

  return (
    <div>
      <Seo
        title={null}
        description="Coffee Cup Supply — cafea boabe și măcinată prăjită din branduri de renume. Livrare națională, prospețime garantată. Prețuri în lei, plată securizată."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Coffee Cup Supply",
            url: window.location.origin,
            logo: window.location.origin + "/favicon.svg",
            email: "office@coffeecup-supply.com",
            telephone: "+40761038086",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Sat Lovrin, nr. 1236 (Hala 2)",
              addressRegion: "Timiș",
              postalCode: "307250",
              addressCountry: "RO",
            },
            sameAs: [],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Coffee Cup Supply",
            url: window.location.origin,
            potentialAction: {
              "@type": "SearchAction",
              target: window.location.origin + "/produse?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ink">
        <motion.div style={{ y: bgY, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Cafea premium" className="h-full w-full object-cover opacity-70" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink/85" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="overline text-sand mb-6"
          >
            Coffee Cup Supply · Magazin de cafea
          </motion.p>
          <h1 className="font-display text-cream text-[3.2rem] leading-[0.95] sm:text-7xl lg:text-[7rem] tracking-tight max-w-5xl">
            <MaskedLines lines={["Cafea de excepție,", "prăjită cu artă."]} delay={0.3} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Link
              to="/produse"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-cream text-ink px-8 py-4 hover:bg-sand transition-colors duration-300"
            >
              <span className="text-sm font-semibold tracking-wide">Descoperă colecția</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-cream/70 max-w-xs text-sm leading-relaxed">
              Lavazza, Kimbo și Jacobs — branduri de renume, livrate acasă.
            </p>
          </motion.div>
        </div>
      </section>

      <BrandMarquee />

      {/* BENEFITS / MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <Reveal>
          <p className="overline text-roast">De ce Coffee Cup Supply</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-3 max-w-3xl">
            Trei promisiuni în fiecare pachet de cafea.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 mt-16">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.n} delay={i * 0.08}>
              <div className="border-t border-line pt-6">
                <div className="flex items-center justify-between">
                  <b.icon size={26} className="text-roast" strokeWidth={1.4} />
                  <span className="font-display text-5xl text-sand/60">{b.n}</span>
                </div>
                <h3 className="font-display text-2xl mt-6">{b.t}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mt-2">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POPULAR PRODUCTS */}
      <section className="bg-stone py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="overline text-roast">Cele mai populare</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-3">Recomandările casei</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/produse"
                data-testid="view-all-products"
                className="group inline-flex items-center gap-2 overline text-ink border-b border-ink pb-1"
              >
                Vezi toate produsele
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {showcase.slice(0, 4).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER — parallax split */}
      <section className="grid lg:grid-cols-2">
        <div className="relative overflow-hidden min-h-[420px] lg:min-h-[640px] bg-ink">
          <img src={LIFESTYLE} alt="Atmosferă cafenea" className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="bg-ink text-cream flex items-center">
          <div className="px-8 md:px-16 py-20 max-w-xl">
            <Reveal>
              <p className="overline text-sand">Despre noi · Capitolul 01</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4 leading-[1.02]">
                Pasiunea pentru cafeaua adevărată.
              </h2>
              <p className="text-cream/70 leading-relaxed mt-6">
                Coffee Cup Supply s-a născut din dorința de a aduce în casa ta cele mai apreciate
                branduri de cafea boabe. Selectăm, păstrăm în condiții optime și livrăm cu grijă cafeaua
                preferată, pentru ca fiecare moment de răsfăț să aibă gustul pe care îl aștepți.
              </p>
              <Link
                to="/despre-noi"
                data-testid="about-teaser-link"
                className="group inline-flex items-center gap-3 mt-10 overline text-sand border-b border-sand pb-1"
              >
                Citește povestea noastră
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <img src={MARBLE} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-cream/80 backdrop-blur-[2px]" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-7xl tracking-tight max-w-4xl mx-auto leading-[1]">
              Începe fiecare zi cu o ceașcă memorabilă.
            </h2>
            <Link
              to="/produse"
              data-testid="cta-band-button"
              className="group inline-flex items-center gap-3 mt-10 bg-ink text-cream px-9 py-4 hover:bg-roast transition-colors duration-300"
            >
              <span className="text-sm font-semibold tracking-wide">Comandă acum</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="bg-stone py-24 md:py-32" data-testid="faq-section">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <Reveal>
              <p className="overline text-roast text-center">Întrebări frecvente</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-3 text-center">Ai o întrebare?</h2>
            </Reveal>
            <FaqAccordion items={faq} />
          </div>
        </section>
      )}
    </div>
  );
}
